'use client';

import { parseSegments } from './_utils/segments';
import { AuthProvider } from '@modules/auth/providers/AuthProvider/AuthProvider';
import { CommonAuthProvider } from '@modules/auth/providers/CommonAuthProvider/CommonAuthProvider';
import { FeatureFlagsProvider } from '@modules/feature-flags/provider/FeatureFlagsProvider';
import { FullStoryCrossDomainSync } from '@modules/shared/analytics/scripts/FullStoryCrossDomainSync';
import { ZeusClientSetupProvider } from '@modules/shared/api/ZeusClientSetupProvider';
import { setLeaderRedirectUrlFromQuery } from '@utils/leaderRedirect';

import { StyleProvider, legacyLogicalPropertiesTransformer, legacyNotSelectorLinter } from '@ant-design/cssinjs';
import { Toast } from '@capra/core';
import { App as AntdApp, ConfigProvider, ThemeConfig } from 'antd';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/en';
import { usePathname, useSearchParams, useSelectedLayoutSegments } from 'next/navigation';
import React from 'react';

// Allow dayjs to be used in the client
// with all necessary plugins enabled
dayjs.extend(utc);
dayjs.extend(relativeTime);
dayjs.extend(quarterOfYear);
dayjs.locale('en');

// It's a recreated theme from the legacy Ant Design v4 theme
const legacyAntdTheme: ThemeConfig = {
  token: {
    borderRadius: 2,
    colorPrimary: '#1890ff',
    wireframe: true,
    fontFamily: "'Open Sans', sans-serif",
  },
  components: {
    Menu: {
      itemBorderRadius: 0,
      subMenuItemBorderRadius: 0,
      itemHoverColor: '#1890ff',
      itemSelectedColor: '#1890ff',
      itemSelectedBg: '#e6f7ff',
      activeBarWidth: 3,
      itemMarginInline: 0,
      itemHoverBg: 'transparent',
      fontSize: 12,
    },
    Alert: {
      defaultPadding: '8px 15px',
    },
    Table: {
      headerSortActiveBg: '#f5f5f5',
      headerFilterHoverBg: 'rgba(0,0,0,.04)',
      headerSortHoverBg: '#f5f5f5',
    },
  },
  hashed: false,
};

interface ClientRootProps {
  children: React.ReactNode;
}

export const ClientRoot: React.FC<ClientRootProps> = ({ children }) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const segments = parseSegments(useSelectedLayoutSegments() || []);
  const organizationId = segments.getIdByName('organizations');

  const from = searchParams?.get('from');

  React.useEffect(() => {
    if (from) {
      setLeaderRedirectUrlFromQuery();
    }
  }, [organizationId, from, pathname]);

  return (
    <StyleProvider
      hashPriority='high'
      layer={false}
      linters={[legacyNotSelectorLinter]}
      transformers={[legacyLogicalPropertiesTransformer]}
    >
      <ConfigProvider theme={legacyAntdTheme}>
        <AntdApp>
          <Toast.Provider />
          <AuthProvider>
            <CommonAuthProvider>
              <ZeusClientSetupProvider>
                <FeatureFlagsProvider>
                  {children}
                  {process.env.FULLSTORY_ENABLED === 'true' && <FullStoryCrossDomainSync />}
                </FeatureFlagsProvider>
              </ZeusClientSetupProvider>
            </CommonAuthProvider>
          </AuthProvider>
        </AntdApp>
      </ConfigProvider>
    </StyleProvider>
  );
};
